import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PROJECTS, HERO_LIST_COUNT, FORCEP } from './projects';

/*
  SECTION 1 — INTERACTIVE FAN DECK (1:1 with Illustrator master)
  Glass plates cascade from bottom-left (front) to top-right (back).
  Hovering a plate (or its name on the right list) lifts it.
  Click → /p/:slug bento page.
*/

const PLATE_COUNT = PROJECTS.length;

const plateStyle = (i) => ({
  left: `calc(${i * 5.4}vw - 6vw)`,
  bottom: `calc(${i * 6.1}vh - 4vh)`,
  zIndex: PLATE_COUNT - i,
});

const Plate = ({ project, index, lifted, dimmed, onHover, onLeave, onClick }) => (
  <motion.button
    type="button"
    aria-label={project.name}
    className="fan-plate absolute block focus:outline-none"
    style={plateStyle(index)}
    initial={{ opacity: 0, y: 90 }}
    animate={{
      opacity: 1,
      y: lifted ? -56 : 0,
      scale: lifted ? 1.03 : 1,
    }}
    transition={{
      opacity: { duration: 0.45, delay: 0.045 * index },
      y: { type: 'spring', stiffness: 260, damping: 24 },
      scale: { type: 'spring', stiffness: 260, damping: 24 },
    }}
    onMouseEnter={onHover}
    onMouseLeave={onLeave}
    onFocus={onHover}
    onBlur={onLeave}
    onClick={onClick}
  >
    <img
      src={project.images[0]}
      alt={project.name}
      loading={index > 4 ? 'lazy' : 'eager'}
      decoding="async"
      draggable={false}
      className="h-full w-full object-cover select-none"
    />
    <span className="fan-plate-gloss" aria-hidden="true" />
    <span className="fan-plate-label">{project.shortName}</span>
  </motion.button>
);

const FanDeck = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const [showMore, setShowMore] = useState(false);

  const heroList = PROJECTS.slice(0, HERO_LIST_COUNT);
  const moreList = PROJECTS.slice(HERO_LIST_COUNT);

  const open = (slug) => navigate(`/p/${slug}`);

  return (
    <section className="landing-font relative h-[100svh] w-full overflow-hidden bg-white text-black">
      {/* Forcep isotype — top left */}
      <Link to="/" aria-label="Forcep Fábrica" className="absolute left-6 top-6 z-[60] md:left-10 md:top-8">
        <img src={FORCEP.isoDark} alt="Forcep Fábrica" className="h-16 w-auto md:h-24" />
      </Link>

      {/* Fan — desktop */}
      <div className="absolute inset-0 hidden md:block" aria-label="Projects fan">
        {PROJECTS.map((p, i) => (
          <Plate
            key={p.slug}
            project={p}
            index={i}
            lifted={hovered === p.slug}
            dimmed={hovered !== null && hovered !== p.slug}
            onHover={() => setHovered(p.slug)}
            onLeave={() => setHovered(null)}
            onClick={() => open(p.slug)}
          />
        ))}
      </div>

      {/* Fan — mobile: horizontal snap strip */}
      <div className="absolute inset-x-0 top-28 bottom-64 flex items-center md:hidden">
        <div className="flex h-full w-full snap-x snap-mandatory gap-4 overflow-x-auto px-6 py-4">
          {PROJECTS.map((p) => (
            <button
              key={p.slug}
              type="button"
              onClick={() => open(p.slug)}
              className="fan-plate-mobile relative h-full shrink-0 snap-center overflow-hidden"
              style={{ aspectRatio: '1 / 1' }}
            >
              <img src={p.images[0]} alt={p.name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              <span className="fan-plate-gloss" aria-hidden="true" />
              <span className="absolute bottom-2 left-2 bg-black px-2 py-1 text-[10px] font-bold tracking-widest text-white">
                {p.shortName}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* PROJECTS list — right */}
      <nav className="absolute bottom-8 right-6 z-[60] text-right md:bottom-14 md:right-12" aria-label="Projects index">
        <h2 className="mb-2 text-[26px] font-bold leading-none tracking-tight md:text-[34px]">PROJECTS:</h2>
        <ul className="space-y-[2px]">
          {heroList.map((p) => (
            <li key={p.slug}>
              <button
                type="button"
                onMouseEnter={() => setHovered(p.slug)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => open(p.slug)}
                className={`text-[20px] font-normal leading-[1.18] tracking-tight transition-opacity duration-150 md:text-[27px] ${
                  hovered && hovered !== p.slug ? 'opacity-40' : 'opacity-100'
                } hover:opacity-100`}
              >
                {p.name}
              </button>
            </li>
          ))}
          {showMore &&
            moreList.map((p) => (
              <motion.li key={p.slug} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}>
                <button
                  type="button"
                  onMouseEnter={() => setHovered(p.slug)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => open(p.slug)}
                  className={`text-[18px] font-normal leading-[1.18] tracking-tight transition-opacity duration-150 md:text-[24px] ${
                    hovered && hovered !== p.slug ? 'opacity-40' : 'opacity-100'
                  } hover:opacity-100`}
                >
                  {p.name}
                </button>
              </motion.li>
            ))}
        </ul>
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="mt-1 text-[12px] font-bold tracking-wide hover:opacity-60 md:text-[13px]"
          aria-expanded={showMore}
        >
          {showMore ? 'LESS−' : 'MORE+'}
        </button>
      </nav>

      {/* OVERVIEW / INDEX — bottom left */}
      <div className="absolute bottom-6 left-6 z-[60] flex gap-2 md:bottom-10 md:left-10">
        <Link
          to="/overview"
          className="rounded-md border border-black/30 bg-white px-4 py-1.5 text-[11px] font-bold tracking-widest text-black shadow-sm transition-colors hover:bg-black hover:text-white"
        >
          OVERVIEW
        </Link>
        <Link
          to="/projects"
          className="rounded-md border border-black/10 bg-white/60 px-4 py-1.5 text-[11px] font-bold tracking-widest text-black/35 transition-colors hover:border-black/30 hover:text-black"
        >
          INDEX
        </Link>
      </div>
    </section>
  );
};

export default FanDeck;
